import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Loader2, Send, ChevronLeft, User, MapPin, Clock, Upload, Sparkles, PhoneCall } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useReveal } from "@/hooks/useReveal";
import Seo from "@/components/Seo";
import { Footer } from "@/App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_IMG = "/img/n148kmw9_image.webp";

const PageNav = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 nav-glass" data-testid="kariera-nav">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="flex items-center justify-between h-16 sm:h-20">
        <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="kariera-logo">
          <img src={LOGO_IMG} alt="EffectDeal" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
          <span className="font-['Cabinet_Grotesk'] font-bold text-lg sm:text-xl tracking-wide">EFFECTDEAL</span>
        </Link>
        <div className="flex items-center gap-3 sm:gap-5">
          <a href="/#vehicles" className="hidden sm:inline text-sm font-medium text-zinc-400 hover:text-white transition-colors">Vozy</a>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-300 hover:text-white transition-colors" data-testid="kariera-back"><ChevronLeft className="w-4 h-4" /> Domů</Link>
          <a href="/prihlaseni" className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-200 border border-white/15 hover:border-orange-400/60 rounded-full px-4 py-2 transition-colors"><User className="w-4 h-4" /> Klientská zóna</a>
        </div>
      </div>
    </div>
  </nav>
);

const ApplyForm = ({ positions }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", position_title: "", message: "" });
  const [cv, setCv] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) { toast.error("Vyplňte jméno a e-mail."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v || ""));
      if (cv) fd.append("cv", cv);
      const res = await fetch(`${BACKEND_URL}/api/careers/apply`, { method: "POST", body: fd });
      if (!res.ok) { const d = await res.json().catch(() => null); throw new Error((d && d.detail) || "Odeslání se nezdařilo."); }
      toast.success("Děkujeme! Vaši přihlášku máme, brzy se ozveme.");
      setF({ name: "", email: "", phone: "", position_title: "", message: "" }); setCv(null);
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8 space-y-3" data-testid="kariera-form">
      <h3 className="font-['Cabinet_Grotesk'] text-xl font-bold mb-1">Přihláška</h3>
      <p className="text-zinc-500 text-sm mb-3">Napište nám pár řádků o sobě a přiložte CV. Nenašli jste svou pozici? Pošlete spontánní přihlášku.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <input className="input-dark" placeholder="Jméno a příjmení *" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="kariera-name" />
        <input className="input-dark" placeholder="E-mail *" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="kariera-email" />
        <input className="input-dark" placeholder="Telefon" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="kariera-phone" />
        <select className="input-dark" value={f.position_title} onChange={(e) => setF({ ...f, position_title: e.target.value })} data-testid="kariera-position">
          <option value="" className="bg-[#0a0a0a]">Spontánní přihláška</option>
          {positions.map((p) => <option key={p.id} value={p.title} className="bg-[#0a0a0a]">{p.title}</option>)}
        </select>
      </div>
      <textarea className="input-dark min-h-[90px]" placeholder="Pár vět o vás – co umíte, proč k nám" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="kariera-message" />
      <label className="block">
        <span className="text-[11px] text-zinc-500 mb-1 flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Životopis / CV (PDF, DOC, foto)</span>
        <input type="file" accept=".pdf,.doc,.docx,image/*" onChange={(e) => setCv(e.target.files?.[0] || null)} className="block w-full text-xs text-zinc-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-orange-500/15 file:text-orange-300 file:text-xs file:cursor-pointer cursor-pointer" data-testid="kariera-cv" />
        {cv && <span className="text-[11px] text-emerald-300 mt-1 block truncate">Přiloží se: {cv.name}</span>}
      </label>
      <button type="submit" disabled={busy} className="btn-accent w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="kariera-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Odeslat přihlášku
      </button>
    </form>
  );
};

const KarieraPage = () => {
  useReveal();
  const [positions, setPositions] = useState([]);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/careers/positions`).then((r) => r.json()).then(setPositions).catch(() => setPositions([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#08080A]">
      <Seo title="Kariéra | EffectDeal" description="Přidejte se k týmu EffectDeal. Otevřené pozice a online přihláška včetně nahrání životopisu." path="/kariera" />
      <PageNav />

      <section className="relative pt-32 sm:pt-40 pb-14 px-4 sm:px-6 overflow-hidden" data-testid="kariera-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/10 via-transparent to-transparent" />
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 text-orange-400 font-medium tracking-widest uppercase text-xs sm:text-sm mb-4 reveal"><Briefcase className="w-4 h-4" /> Kariéra</div>
          <h1 className="font-['Cabinet_Grotesk'] font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight reveal reveal-delay-1">Pojďte dělat <span className="text-orange-400">s námi</span></h1>
          <p className="text-zinc-300 text-base sm:text-lg max-w-2xl mx-auto mt-5 reveal reveal-delay-2">Rosteme a hledáme lidi, kteří to s auty a se zákazníky myslí poctivě. Podívejte se na otevřené pozice nebo nám napište i tak.</p>
          <a href="#prihlaska" className="btn-accent inline-flex mt-8 reveal reveal-delay-3" data-testid="kariera-hero-cta">Chci se přihlásit</a>
        </div>
      </section>

      <section className="py-10 sm:py-16 px-4 sm:px-6" data-testid="kariera-positions">
        <div className="max-w-5xl mx-auto">
          <h2 className="section-title text-2xl sm:text-3xl mb-6">Otevřené pozice</h2>
          {positions.length === 0 ? (
            <div className="glass rounded-2xl p-8 text-center text-zinc-400" data-testid="kariera-no-positions">
              Právě teď nemáme vypsanou konkrétní pozici – ale talent nás vždy zajímá. <span className="text-orange-400">Pošlete spontánní přihlášku níže.</span>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="kariera-positions-list">
              {positions.map((p) => (
                <div key={p.id} className="glass rounded-2xl p-6 hover:border-orange-400/30 transition-colors" data-testid={`kariera-position-${p.id}`}>
                  <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold mb-2">{p.title}</h3>
                  <div className="flex flex-wrap gap-3 text-xs text-zinc-400 mb-3">
                    {p.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.location}</span>}
                    {p.employment_type && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {p.employment_type}</span>}
                  </div>
                  {p.description && <p className="text-zinc-400 text-sm leading-relaxed whitespace-pre-line">{p.description}</p>}
                  <a href="#prihlaska" className="inline-flex items-center gap-1.5 text-sm text-orange-400 font-medium mt-4 hover:text-orange-300"><Sparkles className="w-4 h-4" /> Přihlásit se</a>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section id="prihlaska" className="py-10 sm:py-16 px-4 sm:px-6 bg-[#0a0a0a] scroll-mt-24" data-testid="kariera-apply">
        <div className="max-w-2xl mx-auto">
          <ApplyForm positions={positions} />
        </div>
      </section>

      <Footer />

      <a href="tel:+420725706235" className="sm:hidden fixed bottom-0 left-0 right-0 z-50 flex items-center justify-center gap-2 bg-orange-400 text-black font-semibold py-3.5" style={{ paddingBottom: "calc(0.875rem + env(safe-area-inset-bottom))" }} data-testid="kariera-sticky-call">
        <PhoneCall className="w-4 h-4" /> Zavolat nám
      </a>
    </div>
  );
};

export default KarieraPage;
